/* eslint-disable */
import styled from "styled-components";
import { useDispatch } from "react-redux";
import { useEffect, useState, useRef, useCallback } from "react";
import { Metadata, setMetadata } from "../../state/uwuSlice";
import { ActiveBalances } from "../../components/Web3Automation";
import { fetchTokenIdMulticall } from "../../utils/multicall";
import { OwnerIds } from "../../utils/delegation";
import { HardcodedUwuPreview, UwuPreview } from "./UwuPreview";

const GridContainer = styled.div`
  flex: 1;
  width: 100%;
  background: var(--bg);
  overflow: auto;
  padding-top: 2rem;
  
  /* Always show scrollbar */
  scrollbar-gutter: stable;
  overflow-y: scroll;
`;

const Grid = styled.div`
  display: grid;
  width: 100%;
  grid-template-columns: repeat(auto-fit, minmax(15rem, 1fr));
  grid-gap: 0.5rem;
`;

interface Props {
  active: ActiveBalances[];
  close: () => void;
  cc0: number[];
}

export const UwuBalance = ({ active, close, cc0 }: Props): JSX.Element => {
  const dispatch = useDispatch();
  const [uwus, setUwus] = useState<OwnerIds[]>([]);
  const hasFetchedRef = useRef<Record<string, boolean>>({});
  const lastActiveRef = useRef<string>("");

  // Create a stable key for the active balances
  const activeKey = useCallback(() => {
    return active.map(a => `${a.address}-${a.balance}`).join(",");
  }, [active]);

  useEffect(() => {
    // Skip if we've already fetched for these active balances
    const currentKey = activeKey();
    if (currentKey === lastActiveRef.current) return;
    lastActiveRef.current = currentKey;

    // Fetch all uwu ids
    const fetchUwuIds = async () => {
      if (!active || active.length === 0) return;

      try {
        // Only fetch for addresses we haven't fetched yet
        const addressesToFetch = active.filter(
          a => a.address && !hasFetchedRef.current[a.address]
        );

        if (addressesToFetch.length === 0) return;

        const results = await fetchTokenIdMulticall(addressesToFetch);

        if (results) {
          // Mark addresses as fetched
          addressesToFetch.forEach(a => {
            if (a.address) {
              hasFetchedRef.current[a.address] = true;
            }
          });

          // Merge new results with existing uwus
          const mergedUwus = [...uwus];
          results.forEach(newUwu => {
            if (!newUwu.address) return;
            const existingIndex = mergedUwus.findIndex(
              u => u.address === newUwu.address
            );
            if (existingIndex === -1) {
              mergedUwus.push(newUwu);
            } else {
              mergedUwus[existingIndex] = newUwu;
            }
          });

          setUwus(mergedUwus);
        }
      } catch (error) {
        console.error("Error fetching uwu IDs:", error);
      }
    };

    fetchUwuIds();
  }, [active, activeKey, uwus]);

  return (
    <GridContainer>
      <Grid>
        {uwus &&
          uwus.map(({ ids, address }) => {
            if (!ids || !address) return null;
            return ids.map((j: number) => (
              <UwuPreview
                key={j}
                uwuId={j}
                select={(metadata: Metadata) => {
                  dispatch(setMetadata(metadata));
                  close();
                }}
                coloInfo={active.find(
                  (o: ActiveBalances) => o.address === address
                )}
              />
            ));
          })}

        {cc0 &&
          cc0.map((i: number) => (
            <HardcodedUwuPreview
              key={i}
              uwuId={i}
              select={(metadata: Metadata) => {
                dispatch(setMetadata(metadata));
                close();
              }}
            />
          ))}
      </Grid>
    </GridContainer>
  );
};
